import { useTranslation } from 'react-i18next'
import { ChangeEvent, JSX, RefObject, useEffect, useRef, useState } from 'react'
import styles from './selectUnique.module.scss'
import globalStyles from '@/styles/components.module.scss'
import { Divider, FormControl, ListItemText, ListSubheader, MenuItem, TextField } from '@mui/material'
import {
    ACTION_ITEM_VALUE,
    OptionsGroup,
    SelectActionItem,
    SelectOption,
    SelectOptions,
    ValueType
} from '@/types/Select'

interface SelectUniqueProps<T extends ValueType> {
    id?: string
    label?: string
    noOptionMessage?: string
    selectOptions: SelectOptions<T>
    selectValue: T
    getValueKey?: (value: T) => string
    setSelectValue: (value: T) => void
    readOnly?: boolean
    success?: boolean
    error?: boolean
    fullWidth?: boolean
    input?: boolean
    parent?: boolean
    className?: string
    formClassName?: string
    required?: boolean
    actionItem?: SelectActionItem
    reopenTrigger?: number
    onOpen?: () => void
}

function SelectUnique<T extends ValueType>({
    id,
    label,
    noOptionMessage,
    selectOptions = [],
    selectValue,
    getValueKey = (value) => String(value ?? ''),
    setSelectValue,
    error = false,
    fullWidth = true,
    readOnly = false,
    parent = false,
    className,
    formClassName,
    required = false,
    actionItem,
    reopenTrigger,
    onOpen
}: SelectUniqueProps<T>): JSX.Element {
    const { t } = useTranslation()

    function renderOption(option: SelectOption<T>): JSX.Element {
        return (
            <MenuItem
                key={getValueKey(option.value)}
                value={getValueKey(option.value)}
                className={styles.MenuItem}
                disabled={option.disabled}
                sx={{ whiteSpace: 'normal' }}
            >
                {option.startAdornment && option.startAdornment}
                <ListItemText primary={option.name} />
            </MenuItem>
        )
    }

    const [open, setOpen] = useState<boolean>(false)
    const shouldReopen: RefObject<boolean> = useRef(false)
    const isFirstReopenRender: RefObject<boolean> = useRef(true)

    function handleOpen(): void {
        setOpen(true)
        onOpen?.()
    }

    useEffect((): void => {
        if (isFirstReopenRender.current) {
            isFirstReopenRender.current = false
            return
        }
        if (shouldReopen.current) {
            shouldReopen.current = false
            handleOpen()
        }
    }, [reopenTrigger])

    function renderOptions(optionsGroup: OptionsGroup<T>): (JSX.Element | JSX.Element[] | null)[] {
        const { name, options } = optionsGroup

        return [
            name ? (
                <ListSubheader className={parent ? styles.SubheaderParent : styles.SubheaderDaycare}>
                    {name}
                </ListSubheader>
            ) : null,
            options.map((option: SelectOption<T>): JSX.Element => renderOption(option))
        ]
    }

    function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const value: string = String(e.target.value)

        if (value === ACTION_ITEM_VALUE) {
            shouldReopen.current = true
            actionItem?.onClick()
            return
        }

        const selectedOption: SelectOption<T> | undefined = selectOptions
            .flatMap((optionGroup: OptionsGroup<T>) => optionGroup.options)
            .find((option) => getValueKey(option.value) === value)

        if (selectedOption) {
            setSelectValue(selectedOption.value)
        }
    }
    const selectedKey: string = getValueKey(selectValue)
    const hasSelectedEmptyOption: boolean =
        selectedKey === '' &&
        selectOptions.some((group: OptionsGroup<T>) => group.options.some((option) => getValueKey(option.value) === ''))
    const allOptions: (JSX.Element | JSX.Element[] | null)[] = selectOptions.flatMap(renderOptions)

    if (!selectOptions.some((group) => group.options.length > 0)) {
        allOptions.push(
            <MenuItem key='no-options' disabled>
                {noOptionMessage ?? t('noOptionsAvailable')}
            </MenuItem>
        )
    }

    if (actionItem) {
        allOptions.push(
            <Divider key='action-item-divider' />,
            <MenuItem key='action-item' value={ACTION_ITEM_VALUE} className={styles.ActionItem}>
                {actionItem.icon}
                {actionItem.label}
            </MenuItem>
        )
    }

    return (
        <FormControl
            fullWidth={fullWidth}
            className={`${styles.FormControl} ${formClassName}`}
            disabled={readOnly}
            error={error}
        >
            <TextField
                id={id}
                className={`${className} ${parent ? styles.Parent : styles.Daycare} ${error ? globalStyles.FieldInError : ''}`}
                error={error}
                fullWidth={fullWidth}
                value={selectedKey}
                onChange={handleChange}
                disabled={readOnly}
                select
                label={label}
                required={required}
                slotProps={{
                    inputLabel: hasSelectedEmptyOption ? { shrink: true } : undefined,
                    select: {
                        displayEmpty: hasSelectedEmptyOption,
                        open,
                        onOpen: handleOpen,
                        onClose: () => setOpen(false),
                        MenuProps: {
                            slotProps: {
                                paper: { style: { maxWidth: 500 } }
                            }
                        }
                    }
                }}
            >
                {allOptions}
            </TextField>
        </FormControl>
    )
}

export default SelectUnique
